import { FormEvent, useEffect, useState } from "react";

const styles = {
  page: {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSize: 16,
    lineHeight: 1.6,
    color: "#32373c",
    minHeight: "100vh",
    backgroundColor: "#f7f7f8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 20px",
  } as const,
  card: {
    maxWidth: 480,
    width: "100%",
    backgroundColor: "#ffffff",
    borderRadius: 16,
    padding: "48px 40px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.06)",
  } as const,
  logoWrap: {
    textAlign: "center" as const,
    marginBottom: 32,
  },
  logo: {
    maxWidth: 200,
    maxHeight: 150,
  } as const,
  heading: {
    fontSize: 26,
    fontWeight: 700,
    color: "#32373c",
    textAlign: "center" as const,
    margin: "0 0 8px",
    letterSpacing: "-0.02em",
  } as const,
  paragraph: {
    textAlign: "center" as const,
    marginBottom: 32,
    marginTop: 0,
    color: "#6b7280",
    fontSize: 15,
  } as const,
  label: {
    display: "block",
    marginBottom: 6,
    fontWeight: 500,
    fontSize: 14,
    color: "#32373c",
  } as const,
  input: {
    width: "100%",
    border: "1px solid #d1d5db",
    borderRadius: 10,
    padding: "10px 14px",
    fontSize: 15,
    fontFamily: "inherit",
    backgroundColor: "#ffffff",
    boxSizing: "border-box" as const,
    outline: "none",
    transition: "border-color 0.15s ease",
  } as const,
  checkboxRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    marginTop: 4,
  } as const,
  checkbox: {
    marginTop: 3,
    width: 18,
    height: 18,
    accentColor: "#32373c",
    cursor: "pointer",
  } as const,
  checkboxLabel: {
    fontSize: 14,
    color: "#6b7280",
    cursor: "pointer",
    lineHeight: 1.5,
  } as const,
  button: {
    width: "100%",
    backgroundColor: "#32373c",
    color: "#ffffff",
    fontSize: 15,
    fontWeight: 600,
    fontFamily: "inherit",
    padding: "12px 24px",
    border: "none",
    borderRadius: 9999,
    cursor: "pointer",
    transition: "opacity 0.15s ease",
  } as const,
  error: {
    color: "#dc2626",
    fontSize: 13,
    marginTop: 6,
  } as const,
  successIcon: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    backgroundColor: "#f0fdf4",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 20px",
    fontSize: 28,
    color: "#16a34a",
  } as const,
};

function Unsubscribe() {
  const [email, setEmail] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [unsubscribed, setUnsubscribed] = useState(false);
  const [countdown, setCountdown] = useState(10);

  const urlParams = new URLSearchParams(window.location.search);
  const company = urlParams.get("company") ?? "";
  const logoUrl = `https://storage.googleapis.com/automation-cdn/${company}/logo.png`;
  const colibryHost = (window as any)._env_?.REACT_APP_COLIBRY_SERVICES_HOST ?? "";

  const companyUrl = `https://www.${company}.be`;

  useEffect(() => {
    document.title = "Unsubscribe";
  }, []);

  useEffect(() => {
    if (!unsubscribed) return;
    if (countdown <= 0) {
      window.location.href = companyUrl;
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [unsubscribed, countdown]);

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitted(true);
    if (!confirmed || !isValidEmail) return;

    await fetch(
      `${colibryHost}/api/2/forms/unsubscribe?customer_id=pl1Co9ew&key=AIzaSyBgks68WNcH8RXFGs6arESL2Nft9SBDRXA`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          data: {
            fields: [
              { label: "email", value: email },
              { label: "company", value: company },
            ],
          },
        }),
      },
    );

    setUnsubscribed(true);
  }

  const showEmailWarning = submitted && !isValidEmail;
  const showConfirmWarning = submitted && !confirmed;

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {company && (
          <div style={styles.logoWrap}>
            <a href={`https://www.${company}.be`}>
              <img src={logoUrl} alt="Logo" style={styles.logo} />
            </a>
          </div>
        )}

        {unsubscribed ? (
          <>
            <div style={styles.successIcon}>&#10003;</div>
            <h1 style={styles.heading}>Uitschrijving ontvangen</h1>
            <p style={styles.paragraph}>
              We hebben je uitschrijving goed ontvangen.
              <br />
              Jammer dat je gaat!
            </p>
            <div style={{ textAlign: "center" }}>
              <a href={companyUrl} style={{ ...styles.button, textDecoration: "none", display: "inline-block" }}>
                Ga terug naar de website ({countdown})
              </a>
            </div>
          </>
        ) : (
          <>
            <h1 style={styles.heading}>Je zoekcriteria stopzetten?</h1>
            <p style={styles.paragraph}>
              <i>
                Heb je je droomwoning gevonden of ben je niet langer op zoek? Geen probleem. Vul hieronder je
                e-mailadres in om je uit te schrijven voor nieuwe woningmatches. Mocht je in de toekomst toch weer op
                zoek zijn, helpen we je graag opnieuw.
              </i>
            </p>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 20 }}>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  placeholder="you@example.com"
                  style={styles.input}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                {showEmailWarning && <div style={styles.error}>Gelieve een geldig e-mailadres in te vullen.</div>}
              </div>

              <div style={{ marginBottom: 28 }}>
                <div style={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    id="confirmUnsubscribe"
                    style={styles.checkbox}
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    required
                  />
                  <label htmlFor="confirmUnsubscribe" style={styles.checkboxLabel}>
                    Ik bevestig dat ik me wil uitschrijven
                  </label>
                </div>
                {showConfirmWarning && (
                  <div style={styles.error}>Gelieve te bevestigen dat je je wilt uitschrijven.</div>
                )}
              </div>

              <div>
                <button type="submit" style={styles.button}>
                  Uitschrijven &rarr;
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default Unsubscribe;
